import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import DashboardLayout from "../../../components/DashboardLayout";
import RenderToast from "../../../components/RenderToast";
import PreviewModal from "../../../Modal/Preview";
import ProgressModal from "../../../Modal/ProgressModal";
import ImportOptionsModal from "../../../Modal/ImportOptions";
import API from "../../../utils/api";
import classes from "./ImportProducts.module.css";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export default function ImportProducts() {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [existingSkus, setExistingSkus] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);
  const [progressState, setProgressState] = useState({
    processed: 0,
    imported: 0,
    skipped: 0,
    skippedList: [],
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [importDurationMs, setImportDurationMs] = useState(null);
  const [importStartedAt, setImportStartedAt] = useState(null);
  const fileInputRef = useRef(null);
  const currentUserRef = useRef(readUser());

  const navigate = useNavigate();

  const normalizeKey = (k) => k?.trim().toLowerCase().replace(/\s+/g, "_");

  // LOAD SKUS
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${API.base}/products.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ action: "fetch" }),
        });

        const data = await res.json();
        setExistingSkus(
          new Set(
            (data.data || data || []).map((p) =>
              String(p.sku || "")
                .trim()
                .toLowerCase()
            )
          )
        );
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // LOAD categories & brands
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("authToken");
        const [catRes, brandRes] = await Promise.all([
          fetch(`${API.base}/categories.php`, {
            method: "POST",
            headers: {
              Authorization: token,
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({ action: "fetch" }),
          }),
          fetch(`${API.base}/brands.php`, {
            method: "POST",
            headers: {
              Authorization: token,
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({ action: "fetch" }),
          }),
        ]);

        const catData = await catRes.json();
        const brandData = await brandRes.json();

        setCategories(Array.isArray(catData) ? catData : []);
        setBrands(Array.isArray(brandData) ? brandData : []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const resetState = () => {
    setFile(null);
    setFileName("");
    setPreviewRows([]);
    setHeaders([]);
    setTotalRows(0);
    setShowPreview(false);
    setShowImportOptions(false);
    setShowProgress(false);
    setImportDone(false);
    setUpdateMode(false);
    setProgressState({
      processed: 0,
      imported: 0,
      skipped: 0,
      skippedList: [],
    });
    setImportDurationMs(null);
    setImportStartedAt(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!f.name.toLowerCase().endsWith(".csv"))
      return RenderToast({ type: "error", message: "Only CSV files allowed." });

    setFile(f);
    setFileName(f.name);
  };

  const recordImportSummary = async ({
    rowsTotal,
    importedCount,
    skippedCount,
    durationMs,
    startedAt,
  }) => {
    try {
      const token = localStorage.getItem("authToken") || "";
      const payload = new URLSearchParams({
        action: "record",
        rows_total: rowsTotal,
        rows_imported: importedCount,
        rows_skipped: skippedCount,
        duration_ms: durationMs,
        started_at: startedAt || "",
        file_name: fileName || "",
        user_name: currentUserRef.current?.name || "",
        user_email: currentUserRef.current?.email || "",
      });
      await fetch(`${API.base}/import_logs.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload,
      });
    } catch (err) {
      console.error("Failed to record import summary:", err);
    }
  };

  const parseCSV = async (file) => {
    const text = await file.text();
    const workbook = XLSX.read(text, { type: "string", raw: true });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

    return json.map((row) => {
      const normalized = {};
      Object.entries(row).forEach(([k, v]) => {
        normalized[normalizeKey(k)] = v;
      });
      return normalized;
    });
  };

  const handleOpenPreview = (updateModeFromModal = false) => {
    if (!previewRows.length) {
      return RenderToast({ type: "error", message: "No data to preview" });
    }
    setUpdateMode(updateModeFromModal);
    setShowPreview(true);
  };

  const handleImportClick = async () => {
    if (!file)
      return RenderToast({ type: "error", message: "Select CSV first" });

    setParsing(true);
    try {
      const json = await parseCSV(file);
      if (!json.length) {
        return RenderToast({ type: "error", message: "CSV is empty" });
      }

      setHeaders(Object.keys(json[0]));
      setPreviewRows(json);
      setTotalRows(json.length);
      setShowImportOptions(true);
    } catch (err) {
      RenderToast({ type: "error", message: "Failed to parse CSV" });
    } finally {
      setParsing(false);
    }
  };

  const handleStartImport = async (shouldUpdate = false) => {
    if (!previewRows.length)
      return RenderToast({
        type: "error",
        message: "No rows to import",
      });

    setShowPreview(false);
    setShowImportOptions(false);
    setShowProgress(true);
    setBtnLoading(true);
    setUpdateMode(shouldUpdate);
    setImportDurationMs(null);

    const startedAtIso = new Date().toISOString();
    setImportStartedAt(startedAtIso);
    const timerStart = performance.now();

    const token = localStorage.getItem("authToken");
    const skippedList = [];
    let imported = 0,
      updated = 0,
      skipped = 0,
      processed = 0;

    // Fetch all existing products by SKU if in update mode
    const skuToIdMap = new Map();
    if (shouldUpdate) {
      try {
        const res = await fetch(`${API.base}/products.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ action: "fetch" }),
        });
        const data = await res.json();
        const products = data.data || data || [];
        products.forEach((p) => {
          if (p.sku) {
            skuToIdMap.set(String(p.sku).trim().toLowerCase(), p.id);
          }
        });
      } catch (err) {
        console.error("Failed to fetch existing products:", err);
      }
    }

    for (let i = 0; i < previewRows.length; i++) {
      const row = previewRows[i];
      processed++;

      const sku = String(row["sku"] || "").trim();
      let categoryPath = String(
        row["categories"] || row["category"] || ""
      ).trim();
      const brandName = String(row["brands"] || row["brand"] || "").trim();

      if (!sku) {
        skipped++;
        skippedList.push({
          sku,
          reason: "Missing SKU",
          row: i + 2,
        });

        setProgressState({
          processed,
          imported,
          skipped,
          skippedList: [...skippedList],
        });
        continue;
      }

      // If category is empty, set it to "Uncategorized"
      if (!categoryPath) {
        categoryPath = "Uncategorized";
      }

      const form = new FormData();
      const skuLower = sku.toLowerCase();
      const productId = shouldUpdate ? skuToIdMap.get(skuLower) : null;
      const isUpdate = shouldUpdate && productId;

      if (isUpdate) {
        // Update existing product
        form.append("action", "update");
        form.append("id", productId);
      } else if (shouldUpdate && !productId) {
        // Update mode but product doesn't exist - skip
        skipped++;
        skippedList.push({
          sku,
          reason: "Product not found for update",
          row: i + 2,
        });
        setProgressState({
          processed,
          imported,
          skipped,
          skippedList: [...skippedList],
        });
        continue;
      } else {
        // Add new product
        form.append("action", "add");
      }

      // Always append SKU (but backend won't update it during updates)
      form.append("sku", sku);
      form.append("import_source", "excel");

      // Helper function to append field only if it has a value (for updates) or always (for adds)
      const appendIfValue = (key, value, defaultValue = "") => {
        const val = value || defaultValue;
        if (isUpdate) {
          // In update mode, only append if value exists in Excel (not empty)
          if (value !== undefined && value !== null && String(value).trim() !== "") {
            form.append(key, String(val).trim());
          }
        } else {
          // In add mode, always append (use default if empty)
          form.append(key, String(val).trim());
        }
      };

      // Append fields - only non-empty values in update mode
      appendIfValue("product_name", row["product_name"]);
      appendIfValue("product_short_description", row["product_short_description"]);
      appendIfValue("product_description", row["product_description"]);
      appendIfValue("product_meta_title", row["meta_title"]);
      appendIfValue("product_meta_description", row["meta_description"]);
      
      // For numeric fields, check if value exists
      if (isUpdate) {
        const stockValue = row["instock"] !== undefined ? row["instock"] : (row["stock"] !== undefined ? row["stock"] : null);
        if (stockValue !== undefined && stockValue !== null && stockValue !== "") {
          form.append("product_stocks", stockValue);
        }
      } else {
        form.append("product_stocks", row["instock"] || row["stock"] || 0);
      }
      
      appendIfValue("product_condition", row["condition"]);
      
      if (isUpdate) {
        if (row["regular_price"] !== undefined && row["regular_price"] !== null && row["regular_price"] !== "") {
          form.append("regular_price", row["regular_price"]);
        }
      } else {
        form.append("regular_price", row["regular_price"] || 0);
      }
      
      if (isUpdate) {
        if (row["sales_price"] !== undefined && row["sales_price"] !== null && row["sales_price"] !== "") {
          form.append("sale_price", row["sales_price"]);
        }
      } else {
        form.append("sale_price", row["sales_price"] || 0);
      }
      
      appendIfValue("product_availability", row["availability"]);

      // Status and category - always append for adds, only if provided for updates
      if (!isUpdate) {
        form.append("product_status", "publish");
        form.append("product_category_path", categoryPath);
      } else {
        // For updates, only append if category is provided
        if (categoryPath && categoryPath !== "Uncategorized") {
          form.append("product_category_path", categoryPath);
        }
      }

      // Handle brand - pass brand name so backend can create if doesn't exist
      if (brandName) {
        form.append("product_brand_name", brandName);
      }

      if (row["product_image"] || row["image"])
        form.append("product_image_url", row["product_image"] || row["image"]);

      if (row["product_gallery"] || row["image_gallery"])
        form.append(
          "product_gallery_urls",
          row["product_gallery"] || row["image_gallery"]
        );

      // Validate sale price before sending
      const regularPrice = parseFloat(row["regular_price"] || 0);
      const salePrice = parseFloat(row["sales_price"] || 0);
      if (salePrice > 0 && regularPrice > 0 && salePrice >= regularPrice) {
        skipped++;
        skippedList.push({
          sku,
          reason: "Sale price must be lower than regular price",
          row: i + 2,
        });
        setProgressState({
          processed,
          imported,
          skipped,
          skippedList: [...skippedList],
        });
        continue;
      }

      try {
        const resp = await fetch(`${API.base}/products.php`, {
          method: "POST",
          headers: { Authorization: token },
          body: form,
        });

        // Check if response is OK
        if (!resp.ok) {
          const text = await resp.text();
          let errorMessage = "Backend rejected";
          try {
            const json = JSON.parse(text);
            errorMessage = json.error || errorMessage;
          } catch {
            errorMessage = text || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const json = await resp.json();
        if (json.success) {
          if (shouldUpdate && productId) {
            updated++;
            // Use imported field in progressState to show successful operations
            imported++;
          } else {
            imported++;
            existingSkus.add(skuLower);
            skuToIdMap.set(skuLower, json.id || productId);
          }
        } else {
          skipped++;
          skippedList.push({
            sku,
            reason: json.error || "Backend rejected",
            row: i + 2,
          });
        }
      } catch (err) {
        skipped++;
        skippedList.push({
          sku,
          reason: "Network error: " + err.message,
          row: i + 2,
        });
      }

      setProgressState({
        processed,
        imported,
        skipped,
        skippedList: [...skippedList],
      });
    }

    const durationMs = Math.max(Math.round(performance.now() - timerStart), 0);
    setImportDurationMs(durationMs);
    setBtnLoading(false);
    setImportDone(true);

    await recordImportSummary({
      rowsTotal: previewRows.length,
      importedCount: shouldUpdate ? updated : imported,
      skippedCount: skipped,
      durationMs,
      startedAt: startedAtIso,
    });

    // Toast
    const successCount = shouldUpdate ? updated : imported;
    RenderToast({
      type: successCount > 0 ? "success" : "error",
      message:
        successCount > 0
          ? shouldUpdate
            ? `Updated ${updated} products. ${skipped} skipped.`
            : `Imported ${imported} products. ${skipped} skipped.`
          : `Import finished. ${skipped} skipped.`,
    });
  };

  return (
    <DashboardLayout>
      <div className={classes.container}>
        <h3 className={classes.title}>Import Products</h3>

        {/* ---- Instruction Card ---- */}
        <div className={classes.card}>
          <div className={classes.instructionsBox}>
            <h4>Instructions</h4>
            <ul className={classes.instructionsList}>
              <li>Upload only CSV files saved in UTF-8 format.</li>
              <li>Make sure SKU field is included (Category is optional - products without category will be added to Uncategorized).</li>
              <li>Preview your file before importing.</li>
              <li>Large files may take a few moments to process.</li>
            </ul>
          </div>

          {/* ---- Drag & Drop File Upload ---- */}
          <label className={classes.uploadBox}>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileChange}
              disabled={parsing || btnLoading}
            />
            <div className={classes.uploadTitle}>Click to Select CSV File</div>
            <div style={{ color: "#666" }}>Only CSV files allowed</div>
            {fileName && (
              <div className={classes.selectedFile}>📄 {fileName}</div>
            )}
          </label>

          {/* ---- Buttons ---- */}
          <div className={classes.buttonRow}>
            <button
              className={`${classes.btn} ${classes.btnPrimary}`}
              disabled={!file || parsing || btnLoading}
              onClick={handleImportClick}
            >
              {parsing ? "Processing..." : "Import"}
            </button>

            <button
              className={`${classes.btn} ${classes.btnSecondary}`}
              onClick={resetState}
              disabled={parsing || btnLoading}
            >
              Reset
            </button>
          </div>

          {/* Notes */}
          <div className={classes.notes}>
            <span>✓ Max recommended rows: 10,000</span>
            <span>✓ CSV must contain column headers</span>
          </div>
        </div>

        {/* ---- Import Options Modal ---- */}
        {showImportOptions && (
          <ImportOptionsModal
            onClose={() => setShowImportOptions(false)}
            onPreview={handleOpenPreview}
            onImport={handleStartImport}
            loading={btnLoading}
          />
        )}

        {/* ---- Preview Modal ---- */}
        {showPreview && (
          <PreviewModal
            headers={headers}
            rows={previewRows}
            total={totalRows}
            onClose={() => setShowPreview(false)}
            onStart={() => handleStartImport(updateMode)}
            loadingStart={btnLoading}
          />
        )}

        {/* ---- Progress Modal ---- */}
        {showProgress && (
          <ProgressModal
            progressState={progressState}
            total={previewRows.length}
            importDone={importDone}
            durationMs={importDurationMs}
            startedAt={importStartedAt}
            onViewProducts={() => navigate("/product/view")}
            onClose={() => {
              setShowProgress(false);
              resetState();
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
